import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface Message {
  id: number;
  type: string;
  content: string;
  timestamp: string;
  icon: string;
  stats?: {
    value: string;
    label: string;
  }[];
}

const messages: Message[] = [
  {
    id: 1,
    type: 'order',
    content: 'Nouvelle commande Shopify : #4893 - 219,00€ ✅',
    timestamp: '14:32',
    icon: '🛒',
    stats: [
      { value: '219,00€', label: 'Montant' },
      { value: 'FR', label: 'Pays' }
    ]
  },
  {
    id: 2,
    type: 'support',
    content: 'Nouveau ticket support de Sophie (urgent) - canal : email',
    timestamp: '14:33',
    icon: '🙋‍♂️',
    stats: [
      { value: 'Urgent', label: 'Priorité' },
      { value: 'Email', label: 'Canal' }
    ]
  },
  {
    id: 3,
    type: 'server',
    content: 'Serveur #2 – CPU > 90% | Instance AWS - Europe',
    timestamp: '14:34',
    icon: '🚨',
    stats: [
      { value: '90%', label: 'CPU' },
      { value: 'EU', label: 'Région' }
    ]
  },
  {
    id: 4,
    type: 'ai',
    content: 'Requête IA : article SEO généré pour "Formation Branding 2024"',
    timestamp: '14:35',
    icon: '🧠',
    stats: [
      { value: '2500', label: 'Mots' },
      { value: 'SEO', label: 'Type' }
    ]
  },
  {
    id: 5,
    type: 'social',
    content: 'Nouvelle connexion Messenger – client : Maxime L. (retargeting actif)',
    timestamp: '14:36',
    icon: '📲',
    stats: [
      { value: 'FB', label: 'Source' },
      { value: 'Actif', label: 'Status' }
    ]
  },
  {
    id: 6,
    type: 'crm',
    content: 'Sync CRM / HubSpot : 3 nouveaux leads ajoutés',
    timestamp: '14:37',
    icon: '🔄',
    stats: [
      { value: '3', label: 'Leads' },
      { value: 'B2B', label: 'Type' }
    ]
  },
  {
    id: 7,
    type: 'ads',
    content: 'Google Ads : ROI > 300% sur campagne #Funnel-PRO',
    timestamp: '14:38',
    icon: '📈',
    stats: [
      { value: '300%', label: 'ROI' },
      { value: '0.45€', label: 'CPC' }
    ]
  }
];

const integrations = [
  { name: 'Facebook Ads', icon: 'https://cdn.worldvectorlogo.com/logos/facebook-ads.svg' },
  { name: 'Shopify', icon: 'https://cdn.worldvectorlogo.com/logos/shopify.svg' },
  { name: 'OpenAI', icon: 'https://cdn.worldvectorlogo.com/logos/openai-2.svg' },
  { name: 'AWS', icon: 'https://cdn.worldvectorlogo.com/logos/aws-2.svg' },
  { name: 'HubSpot', icon: 'https://cdn.worldvectorlogo.com/logos/hubspot.svg' },
  { name: 'Slack', icon: 'https://cdn.worldvectorlogo.com/logos/slack-new-logo.svg' },
  { name: 'Gmail', icon: 'https://cdn.worldvectorlogo.com/logos/gmail-icon.svg' },
  { name: 'WhatsApp', icon: 'https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg' }
];

const BusinessBotSection = () => {
  const [visibleMessages, setVisibleMessages] = useState<Message[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      if (currentIndex < messages.length) {
        setIsTyping(true);
        setTimeout(() => {
          setVisibleMessages(prev => [...prev, messages[currentIndex]]);
          setIsTyping(false);
          setCurrentIndex(prev => prev + 1);
        }, 1000);
      } else {
        setVisibleMessages([]);
        setCurrentIndex(0);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [currentIndex]);

  return (
    <section className="py-20 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--neon-orange)_0%,_transparent_100%)] opacity-20" />
        <motion.div
          className="absolute top-20 right-20 w-96 h-96 bg-neon-orange/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Side - Chat Interface */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-[#17212b] rounded-xl overflow-hidden shadow-2xl"
          >
            {/* Chat Header */}
            <div className="bg-[#242f3d] p-4 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-neon-orange/20 flex items-center justify-center">
                    <span className="text-3xl">🤖</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white text-lg">CSD_Bot</h3>
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                        En ligne
                      </span>
                      <span className="text-gray-500">|</span>
                      <span>Temps réel</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <button className="text-gray-400 hover:text-white transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </button>
                  <button className="text-gray-400 hover:text-white transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Chat Messages */}
            <div className="h-[600px] overflow-y-auto p-4 space-y-4" style={{ backgroundColor: '#0E1621' }}>
              {visibleMessages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-[#182533] rounded-lg p-4 hover:bg-[#1c2a3a] transition-colors duration-300"
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{message.icon}</span>
                    <div className="flex-1">
                      <div className="text-white mb-2">{message.content}</div>
                      {message.stats && (
                        <div className="grid grid-cols-2 gap-2">
                          {message.stats.map((stat, index) => (
                            <div key={index} className="bg-[#242f3d] rounded-lg p-2 text-center">
                              <div className="text-neon-orange font-semibold">{stat.value}</div>
                              <div className="text-gray-400 text-sm">{stat.label}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <span className="text-sm text-gray-500">{message.timestamp}</span>
                  </div>
                </motion.div>
              ))}
              {isTyping && (
                <div className="flex items-center gap-2 text-gray-400 p-2">
                  <motion.div
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="text-sm"
                  >
                    CSD_Bot est en train d'écrire
                  </motion.div>
                  <div className="flex gap-1">
                    <motion.div
                      animate={{ y: [0, -3, 0] }}
                      transition={{ duration: 0.6, repeat: Infinity }}
                      className="w-1 h-1 rounded-full bg-gray-400"
                    />
                    <motion.div
                      animate={{ y: [0, -3, 0] }}
                      transition={{ duration: 0.6, delay: 0.2, repeat: Infinity }}
                      className="w-1 h-1 rounded-full bg-gray-400"
                    />
                    <motion.div
                      animate={{ y: [0, -3, 0] }}
                      transition={{ duration: 0.6, delay: 0.4, repeat: Infinity }}
                      className="w-1 h-1 rounded-full bg-gray-400"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Chat Input */}
            <div className="bg-[#242f3d] p-4 flex items-center gap-4">
              <div className="flex-1 bg-[#17212b] rounded-full px-6 py-3 text-gray-300">
                Écrire un message...
              </div>
              <button className="text-neon-orange hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              <button className="text-neon-orange hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
              </button>
              <button className="bg-neon-orange text-white p-2 rounded-full hover:bg-[#FF4500] transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </button>
            </div>
          </motion.div>

          {/* Right Side - Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div>
              <motion.h2
                className="text-4xl font-bold text-white mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                Votre business
                <span className="bg-gradient-to-r from-neon-orange to-[#FF4500] bg-clip-text text-transparent"> connecté et intelligent</span>
              </motion.h2>
              <motion.p
                className="text-gray-300 text-lg leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Centralisez toutes vos notifications business dans une interface unique. 
                Restez informé en temps réel de vos ventes, support client, performances serveur et bien plus encore.
              </motion.p>
            </div>

            {/* Integration Grid */}
            <div>
              <motion.h3
                className="text-xl font-semibold text-white mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                Intégrations disponibles
              </motion.h3>
              <div className="grid grid-cols-4 gap-4">
                {integrations.map((integration, index) => (
                  <motion.div
                    key={integration.name}
                    initial={{ opacity: 0, scale: 0.5 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-[#182533] rounded-lg p-4 flex items-center justify-center group hover:bg-[#1c2a3a] transition-all duration-300"
                  >
                    <img
                      src={integration.icon}
                      alt={integration.name}
                      className="w-8 h-8 object-contain group-hover:scale-110 transition-transform duration-300"
                    />
                  </motion.div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Link
                to="/services"
                className="inline-block bg-gradient-to-r from-neon-orange to-[#FF4500] text-white px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden group"
              >
                <span className="relative z-10">Créer mon bot business personnalisé</span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "100%" }}
                  transition={{ duration: 1 }}
                />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default BusinessBotSection;